/**
 * Le opzioni di una scelta (CF05) — CL02.
 *
 * L'`id` di un'opzione e' **l'indirizzo del valore**: `userdata` di un processo
 * gia' avviato contiene quell'id, non la label. Percio' qui non esiste nessuna
 * operazione che rinumera o ricalcola un id: si genera una volta, alla
 * creazione, e da li' in poi la label si cambia quanto si vuole.
 *
 * La conseguenza pratica: rinominare non e' rimuovere-piu'-aggiungere. Chi
 * scrivesse l'editor come "cancella la voce e rimettila col nome nuovo"
 * orfanerebbe in silenzio ogni valore che la sceglieva.
 *
 * Le mutazioni restituiscono array nuovi: la bozza del configuratore si annulla
 * smettendo di guardarla, non ripristinandola.
 */

import type { OptionDefinition } from "model/CustomFields";
import { LIMITS } from "./fieldConfig";
import { newFieldId } from "./fieldTypes";
import { codePointLength } from "./legacyAdapter";

/**
 * Una voce appena creata: id stabile, label vuota da riempire.
 *
 * L'id viene da `newFieldId`, non da `create_UUID` di `common`: quel barrel
 * trascina dentro l'intera applicazione (e con essa `react-beautiful-dnd`, che
 * e' un ES Module e non si lascia richiedere da un test), mentre questo layer
 * deve restare caricabile da solo.
 */
export const newOption = (): OptionDefinition => ({
  id: newFieldId(),
  label: "",
});

/**
 * Aggiunge in coda. Rifiuta oltre il limite del contratto.
 *
 * Con `emptyKey` la voce nasce **senza chiave**: è la modalità «Dinamico», dove
 * la chiave la scrive il progettista e un id generato in anticipo sarebbe un
 * valore che nessuno ha scelto — e che si potrebbe dimenticare di sostituire.
 */
export function addOption(
  options: readonly OptionDefinition[],
  emptyKey = false
): OptionDefinition[] {
  if (options.length >= LIMITS.maxOptionsPerField) return [...options];
  return [...options, emptyKey ? { id: "", label: "" } : newOption()];
}

/** Sposta per **posizione**: le righe dinamiche possono avere id vuoti. */
export function moveOptionFromTo(
  options: readonly OptionDefinition[],
  from: number,
  to: number
): OptionDefinition[] {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= options.length ||
    to >= options.length
  ) {
    return [...options];
  }
  const next = [...options];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Toglie una voce. Le altre restano dove sono, id compresi. */
export function removeOption(
  options: readonly OptionDefinition[],
  id: string
): OptionDefinition[] {
  return options.filter((option) => option.id !== id);
}

/**
 * Cambia la label, lasciando l'id.
 *
 * E' l'operazione che rende sicura la rinomina: i valori memorizzati puntano
 * all'id, quindi non si accorgono di niente.
 */
export function renameOption(
  options: readonly OptionDefinition[],
  id: string,
  label: string
): OptionDefinition[] {
  return options.map((option) =>
    option.id === id ? { ...option, label } : option
  );
}

/** Cambia il colore, o lo toglie con `null`. */
export function recolorOption(
  options: readonly OptionDefinition[],
  id: string,
  color: string | null
): OptionDefinition[] {
  return options.map((option) =>
    option.id === id ? { ...option, color } : option
  );
}

/** Sposta una voce di una posizione. L'ordine e' presentazione, non indirizzo. */
export function moveOption(
  options: readonly OptionDefinition[],
  id: string,
  direction: "up" | "down"
): OptionDefinition[] {
  const index = options.findIndex((option) => option.id === id);
  if (index === -1) return [...options];
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= options.length) return [...options];

  const next = [...options];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/**
 * Sposta una voce **in una posizione precisa**, non di un passo.
 *
 * E' quello che serve al trascinamento: chi rilascia indica la destinazione, non
 * quante posizioni salire. Le frecce restano servite da `moveOption`, che ragiona
 * per passi perche' un pulsante ha solo due direzioni.
 *
 * L'ordine e' presentazione: nessun id cambia, quindi i valori gia' memorizzati
 * continuano a puntare alla stessa voce.
 */
export function moveOptionTo(
  options: readonly OptionDefinition[],
  id: string,
  toIndex: number
): OptionDefinition[] {
  const from = options.findIndex((option) => option.id === id);
  if (from === -1 || toIndex < 0 || toIndex >= options.length || toIndex === from) {
    // Una voce che non c'è, o lasciata dov'era: nessuna modifica, ma una copia —
    // il chiamante la mette nello stato, e lo stato non si muta.
    return [...options];
  }

  const next = [...options];
  const [moved] = next.splice(from, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

/**
 * I problemi delle opzioni, come chiavi di traduzione.
 *
 * L'unicita' degli id la verifica `choiceConfigIssues` — non la riscrivo qui.
 * Qui sta cio' che quella non guarda: la label vuota e la sua lunghezza in
 * **code point**, perche' il contratto conta i caratteri e un'emoji ne e' uno.
 *
 * Una label vuota e' un problema e non un id duplicato: sono due cose diverse e
 * chi legge deve sapere quale delle due ha.
 */
export function optionIssues(
  options: readonly OptionDefinition[]
): { id: string; issue: string }[] {
  const issues: { id: string; issue: string }[] = [];

  for (const option of options) {
    if (option.label.trim() === "") {
      issues.push({ id: option.id, issue: "custom_fields.error.validation_failed" });
      continue;
    }
    if (codePointLength(option.label) > LIMITS.maxLabelCodePoints) {
      issues.push({ id: option.id, issue: "custom_fields.error.validation_failed" });
    }
  }

  return issues;
}

/** Vero quando si puo' aggiungere un'altra voce. */
export const canAddOption = (options: readonly OptionDefinition[]): boolean =>
  options.length < LIMITS.maxOptionsPerField;
